import { useQuery } from '@tanstack/vue-query';
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from '@/shared/env';

// Unscoped cache entry — used by popper.vue for all-channels lookups.
// Do NOT use this in per-channel compliance keyword pages; use
// complianceKeywordsByChannelQueryOptions instead.
export const complianceKeywordsQueryOptions = {
  queryKey: ['compliance-keywords'],
  queryFn: async () => {
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'social/v1/compliance');
    return Array.isArray(r) ? r : (r?.data || []);
  },
  staleTime: Infinity,
};

/**
 * Returns TanStack Query options scoped to a specific channel.
 * Each channel gets its own cache entry under ['compliance-keywords', channel].
 * The API is called with ?channel=<channel> so each request is truly scoped —
 * a WhatsApp mutation invalidates only ['compliance-keywords', 'whatsapp'] and
 * re-fetches only the whatsapp-filtered dataset; SMS data is served from its
 * own cache entry with no additional network call.
 *
 * Use queryClient.fetchQuery(complianceKeywordsByChannelQueryOptions(channel))
 * in load methods so that after invalidateQueries the next fetch re-fetches
 * (fetchQuery respects stale state; ensureQueryData does not).
 *
 * @param {'whatsapp'|'sms'|'googlercs'} channel
 */
export const complianceKeywordsByChannelQueryOptions = (channel) => ({
  queryKey: ['compliance-keywords', channel],
  queryFn: async () => {
    const r = await apiInterceptor.get(
      environment('VUE_APP_API_URL') + `social/v1/compliance?channel=${channel}`
    );
    const data = Array.isArray(r) ? r : (r?.data || []);
    // Client-side guard: filter by channel in case the backend does not strictly
    // honour the ?channel= query parameter.
    return data.filter(item => item.channel === channel);
  },
  // staleTime: 0 ensures every fetchQuery call hits the network — compliance data
  // can be changed by any user of the shop, so it must never be served from cache.
  staleTime: 0,
});

/**
 * Composition API hook for compliance keywords, scoped to a channel.
 *
 * @param {'whatsapp'|'sms'|'googlercs'} channel
 */
export function useComplianceKeywords(channel) {
  return useQuery(complianceKeywordsByChannelQueryOptions(channel));
}
